import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { createCustomer } from './api';
import { useIsMobile } from '../../hooks/useIsMobile';
const emptyAddress = () => ({ label: '', street: '', city: '', state: '', zip: '' });
const emptyPhone = () => ({ type: 'mobile', number: '' });
function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
export const CustomerCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [name, setName] = useState('');
    const [type, setType] = useState('residential');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [addresses, setAddresses] = useState([emptyAddress()]);
    const [phones, setPhones] = useState([]);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const validate = () => {
        const errs = {};
        if (!name.trim())
            errs['name'] = 'Customer name is required';
        if (!firstName.trim())
            errs['firstName'] = 'First name is required';
        if (!lastName.trim())
            errs['lastName'] = 'Last name is required';
        if (email && !validateEmail(email))
            errs['email'] = 'Invalid email address';
        addresses.forEach((addr, idx) => {
            if (!addr.label.trim())
                errs[`addr_${idx}_label`] = 'Label is required';
            if (!addr.street.trim())
                errs[`addr_${idx}_street`] = 'Street is required';
            if (!addr.city.trim())
                errs[`addr_${idx}_city`] = 'City is required';
            if (!addr.state.trim())
                errs[`addr_${idx}_state`] = 'State is required';
            if (!addr.zip.trim())
                errs[`addr_${idx}_zip`] = 'ZIP is required';
        });
        phones.forEach((phone, idx) => {
            if (!phone.number.trim())
                errs[`phone_${idx}_number`] = 'Phone number is required';
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate())
            return;
        const input = {
            name: name.trim(),
            type,
            primaryContact: { firstName: firstName.trim(), lastName: lastName.trim() },
            email: email.trim() || undefined,
            phones: phones.length > 0 ? phones : undefined,
            addresses,
        };
        setSubmitting(true);
        setApiError(null);
        try {
            const created = await createCustomer(input);
            navigate(`/customers/${created.id}`);
        }
        catch (e) {
            // Try to parse field-level errors from 422 responses
            if (e.errors && Array.isArray(e.errors)) {
                const errs = {};
                for (const fe of e.errors) {
                    errs[fe.field ?? 'general'] = fe.message ?? 'Invalid';
                }
                setErrors(errs);
            }
            else {
                setApiError(e.message ?? 'Failed to create customer');
            }
        }
        finally {
            setSubmitting(false);
        }
    };
    const updateAddress = (idx, field, value) => {
        setAddresses((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };
    const addAddress = () => setAddresses((prev) => [...prev, emptyAddress()]);
    const removeAddress = (idx) => setAddresses((prev) => prev.filter((_, i) => i !== idx));
    const updatePhone = (idx, field, value) => {
        setPhones((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };
    const addPhone = () => setPhones((prev) => [...prev, emptyPhone()]);
    const removePhone = (idx) => setPhones((prev) => prev.filter((_, i) => i !== idx));
    const cardStyle = {
        ...styles.card,
        padding: isMobile ? '20px 16px' : '28px 32px',
    };
    const mobileInputStyle = (hasError) => ({
        ...inputStyle(hasError),
        fontSize: isMobile ? '16px' : '15px',
    });
    const mobileSelectStyle = {
        ...styles.input,
        maxWidth: '130px',
        fontSize: isMobile ? '16px' : '15px',
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/customers'), children: "\u2190 Customers" }), _jsx("h2", { style: styles.heading, children: "New Customer" }), _jsx("p", { style: styles.subheading, children: "Fill in the details to create a new customer record." }), _jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [apiError && _jsx("div", { style: styles.errorBanner, children: apiError }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Basic Information" }), _jsxs("div", { style: styles.fieldGrid, children: [_jsx(FormField, { label: "Customer Name *", error: errors['name'], children: _jsx("input", { style: mobileInputStyle(!!errors['name']), type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Acme Corp" }) }), _jsx(FormField, { label: "Type *", children: _jsx("div", { style: styles.radioGroup, children: ['commercial', 'residential'].map((t) => (_jsxs("label", { style: styles.radioLabel, children: [_jsx("input", { type: "radio", name: "type", value: t, checked: type === t, onChange: () => setType(t), style: { marginRight: '6px' } }), t.charAt(0).toUpperCase() + t.slice(1)] }, t))) }) }), _jsx(FormField, { label: "Email", error: errors['email'], children: _jsx("input", { style: mobileInputStyle(!!errors['email']), type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: "email@example.com" }) })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Primary Contact" }), _jsxs("div", { style: styles.fieldGrid, children: [_jsx(FormField, { label: "First Name *", error: errors['firstName'], children: _jsx("input", { style: mobileInputStyle(!!errors['firstName']), type: "text", value: firstName, onChange: (e) => setFirstName(e.target.value), placeholder: "Jane" }) }), _jsx(FormField, { label: "Last Name *", error: errors['lastName'], children: _jsx("input", { style: mobileInputStyle(!!errors['lastName']), type: "text", value: lastName, onChange: (e) => setLastName(e.target.value), placeholder: "Smith" }) })] })] }), _jsxs("div", { style: cardStyle, children: [_jsxs("div", { style: styles.sectionHeaderRow, children: [_jsx("h3", { style: styles.sectionTitle, children: "Addresses" }), _jsx("button", { type: "button", style: styles.addItemButton, onClick: addAddress, children: "+ Add Address" })] }), addresses.map((addr, idx) => (_jsxs("div", { style: styles.subCard, children: [_jsxs("div", { style: styles.subCardHeader, children: [_jsxs("span", { style: styles.subCardIndex, children: ["Address ", idx + 1] }), addresses.length > 1 && (_jsx("button", { type: "button", style: styles.removeButton, onClick: () => removeAddress(idx), children: "Remove" }))] }), _jsxs("div", { style: styles.fieldGrid, children: [_jsx(FormField, { label: "Label *", error: errors[`addr_${idx}_label`], children: _jsx("input", { style: mobileInputStyle(!!errors[`addr_${idx}_label`]), type: "text", value: addr.label, onChange: (e) => updateAddress(idx, 'label', e.target.value), placeholder: "e.g. Main Office" }) }), _jsx(FormField, { label: "Street *", error: errors[`addr_${idx}_street`], children: _jsx("input", { style: mobileInputStyle(!!errors[`addr_${idx}_street`]), type: "text", value: addr.street, onChange: (e) => updateAddress(idx, 'street', e.target.value), placeholder: "123 Main St" }) }), _jsx(FormField, { label: "City *", error: errors[`addr_${idx}_city`], children: _jsx("input", { style: mobileInputStyle(!!errors[`addr_${idx}_city`]), type: "text", value: addr.city, onChange: (e) => updateAddress(idx, 'city', e.target.value), placeholder: "Springfield" }) }), _jsx(FormField, { label: "State *", error: errors[`addr_${idx}_state`], children: _jsx("input", { style: mobileInputStyle(!!errors[`addr_${idx}_state`]), type: "text", value: addr.state, onChange: (e) => updateAddress(idx, 'state', e.target.value), placeholder: "IL", maxLength: 2 }) }), _jsx(FormField, { label: "ZIP *", error: errors[`addr_${idx}_zip`], children: _jsx("input", { style: mobileInputStyle(!!errors[`addr_${idx}_zip`]), type: "text", value: addr.zip, onChange: (e) => updateAddress(idx, 'zip', e.target.value), placeholder: "62701" }) })] })] }, idx)))] }), _jsxs("div", { style: cardStyle, children: [_jsxs("div", { style: styles.sectionHeaderRow, children: [_jsx("h3", { style: styles.sectionTitle, children: "Phone Numbers" }), _jsx("button", { type: "button", style: styles.addItemButton, onClick: addPhone, children: "+ Add Phone" })] }), phones.length === 0 && (_jsx("p", { style: styles.emptySubText, children: "No phone numbers added yet." })), phones.map((phone, idx) => (_jsxs("div", { style: styles.phoneRow, children: [_jsxs("select", { style: mobileSelectStyle, value: phone.type, onChange: (e) => updatePhone(idx, 'type', e.target.value), children: [_jsx("option", { value: "mobile", children: "Mobile" }), _jsx("option", { value: "office", children: "Office" }), _jsx("option", { value: "fax", children: "Fax" }), _jsx("option", { value: "other", children: "Other" })] }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("input", { style: mobileInputStyle(!!errors[`phone_${idx}_number`]), type: "tel", value: phone.number, onChange: (e) => updatePhone(idx, 'number', e.target.value), placeholder: "(555) 555-5555" }), errors[`phone_${idx}_number`] && (_jsx("p", { style: styles.fieldError, children: errors[`phone_${idx}_number`] }))] }), _jsx("button", { type: "button", style: styles.removeButton, onClick: () => removePhone(idx), children: "Remove" })] }, idx)))] }), _jsxs("div", { style: styles.submitRow, children: [_jsx("button", { type: "button", style: styles.ghostButton, onClick: () => navigate('/customers'), disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", style: styles.primaryButton, disabled: submitting, children: submitting ? 'Creating…' : 'Create Customer' })] })] })] }) }));
};
const FormField = ({ label, error, children }) => (_jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: label }), children, error && _jsx("p", { style: styles.fieldError, children: error })] }));
const inputStyle = (hasError) => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '15px',
    border: `1px solid ${hasError ? 'rgba(255,59,48,0.6)' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.8)',
    color: '#1d1d1f',
    outline: 'none',
    boxSizing: 'border-box',
});
const styles = {
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: '0 0 32px 0',
        color: '#86868b',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    sectionHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px 24px',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    fieldLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    fieldError: {
        margin: '4px 0 0 0',
        fontSize: '12px',
        color: '#c0392b',
    },
    radioGroup: {
        display: 'flex',
        gap: '20px',
        paddingTop: '4px',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '15px',
        color: '#1d1d1f',
        cursor: 'pointer',
    },
    subCard: {
        padding: '20px',
        backgroundColor: 'rgba(0,0,0,0.025)',
        borderRadius: '14px',
        marginBottom: '12px',
    },
    subCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
    },
    subCardIndex: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    phoneRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '12px',
    },
    input: {
        padding: '10px 12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
    },
    addItemButton: {
        padding: '7px 16px',
        backgroundColor: 'transparent',
        color: '#0071e3',
        border: '1px solid #0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    removeButton: {
        padding: '7px 14px',
        backgroundColor: 'transparent',
        color: '#c0392b',
        border: '1px solid rgba(255,59,48,0.4)',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    emptySubText: {
        color: '#86868b',
        fontSize: '14px',
        margin: 0,
    },
    submitRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '40px',
    },
    primaryButton: {
        padding: '12px 28px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
        minHeight: '44px',
    },
    ghostButton: {
        padding: '12px 24px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        minHeight: '44px',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '20px',
    },
};
//# sourceMappingURL=CustomerCreate.js.map